const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFB9D1EA" },
};

const SUBHEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

const TABLE_HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFCE4D6" },
};

const THIN_BLACK = {
  top: { style: "thin", color: { argb: "FF000000" } },
  left: { style: "thin", color: { argb: "FF000000" } },
  bottom: { style: "thin", color: { argb: "FF000000" } },
  right: { style: "thin", color: { argb: "FF000000" } },
};

const BODY_FONT = { name: "Calibri", size: 11 };
const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

const COL_QUESTION = 4; // D
const COL_P_START = 5; // E = P1
const COL_P_END = 13; // M = P9
const COL_FREQ_START = 14; // N
const COL_FREQ_END = 22; // V
const PRINCIPLE_COUNT = 9;

const COL_WIDTH_QUESTION = 40;
const COL_WIDTH_PRINCIPLE = 10;
const COL_WIDTH_MERGED_PRINCIPLES = COL_WIDTH_PRINCIPLE * PRINCIPLE_COUNT;
const COL_WIDTH_FULL_TABLE =
  COL_WIDTH_QUESTION + COL_WIDTH_MERGED_PRINCIPLES * 2;

const LINE_HEIGHT = 15;
const DEFAULT_MIN_ROW_HEIGHT = 18;

function estimateRowHeight(text, colWidth, minHeight = DEFAULT_MIN_ROW_HEIGHT) {
  const str = String(text ?? "");
  if (!str.trim()) return minHeight;
  const charsPerLine = Math.max(8, Math.floor(colWidth * 0.85));
  const lineCount = str.split(/\r?\n/).reduce(
    (total, part) => total + Math.max(1, Math.ceil(part.length / charsPerLine)),
    0
  );
  return Math.max(minHeight, lineCount * LINE_HEIGHT + 8);
}

function setAutoRowHeight(ws, row, parts) {
  const height = parts.reduce(
    (max, { text, width }) => Math.max(max, estimateRowHeight(text, width)),
    DEFAULT_MIN_ROW_HEIGHT
  );
  ws.getRow(row).height = height;
}

function borderCell(cell) {
  cell.border = THIN_BLACK;
}

function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  if (options.link) {
    cell.value = { text: value || "View details", hyperlink: options.link };
    cell.font = LINK_FONT;
  } else {
    cell.value = value;
    cell.font = options.bold ? { ...BODY_FONT, bold: true } : BODY_FONT;
  }
  borderCell(cell);
  cell.alignment = {
    horizontal: options.align ?? (options.link ? "center" : "left"),
    vertical: options.vertical || "middle",
    wrapText: true,
  };
  if (options.fill) cell.fill = options.fill;
}

function toYesNoNa(value) {
  if (value === true || value === "true" || value === "yes" || value === "Yes") return "Yes";
  if (value === false || value === "false" || value === "no" || value === "No") return "No";
  if (value === null || value === undefined || value === "") return "NA";
  return String(value);
}

function formatReviewValue(value) {
  if (typeof value === "string" && value && !["true", "false"].includes(value)) return value;
  return toYesNoNa(value);
}

function principleKey(index) {
  return `principle${index + 1}`;
}

function getPrincipleValues(obj, formatter = toYesNoNa) {
  return Array.from({ length: PRINCIPLE_COUNT }, (_, i) =>
    formatter(obj?.[principleKey(i)])
  );
}

function fillPrincipleRow(ws, row, values, startCol = COL_P_START, align = "center") {
  values.forEach((value, i) => {
    setCell(ws, row, startCol + i, value, { align });
  });
}

function styleSectionTitle(ws, row, title, mergeEnd = COL_P_END) {
  ws.mergeCells(row, COL_QUESTION, row, mergeEnd);
  const cell = ws.getCell(row, COL_QUESTION);
  cell.value = title;
  cell.font = { ...BODY_FONT, bold: true };
  cell.fill = HEADER_FILL;
  borderCell(cell);
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  setAutoRowHeight(ws, row, [{ text: title, width: COL_WIDTH_FULL_TABLE }]);
}

function styleSubSectionTitle(ws, row, title, mergeEnd = COL_P_END) {
  ws.mergeCells(row, COL_QUESTION, row, mergeEnd);
  const cell = ws.getCell(row, COL_QUESTION);
  cell.value = title;
  cell.font = { ...BODY_FONT, bold: true };
  cell.fill = SUBHEADER_FILL;
  borderCell(cell);
  cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  setAutoRowHeight(ws, row, [{ text: title, width: COL_WIDTH_FULL_TABLE }]);
}

function addPolicyHeaderRow(ws, row) {
  setCell(ws, row, COL_QUESTION, "Disclosure Questions", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  for (let i = 0; i < PRINCIPLE_COUNT; i++) {
    setCell(ws, row, COL_P_START + i, `P${i + 1}`, {
      bold: true,
      fill: TABLE_HEADER_FILL,
      align: "center",
    });
  }
  return row + 1;
}

function addQuestionPrincipleRow(ws, row, label, values, options = {}) {
  setCell(ws, row, COL_QUESTION, label, {
    align: "left",
    bold: options.bold,
    vertical: "top",
  });
  fillPrincipleRow(ws, row, values, COL_P_START, options.align || "center");
  setAutoRowHeight(ws, row, [{ text: label, width: COL_WIDTH_QUESTION }]);
  return row + 1;
}

function addTextOrDetailsRow(ws, row, label, text) {
  setCell(ws, row, COL_QUESTION, label, { align: "left", vertical: "top" });
  const display = text ? String(text) : "View details";
  ws.mergeCells(row, COL_P_START, row, COL_P_END);
  setCell(ws, row, COL_P_START, display, {
    align: text ? "left" : "center",
    vertical: "top",
  });
  for (let col = COL_P_START + 1; col <= COL_P_END; col++) {
    borderCell(ws.getCell(row, col));
  }
  setAutoRowHeight(ws, row, [
    { text: label, width: COL_WIDTH_QUESTION },
    { text: display, width: COL_WIDTH_MERGED_PRINCIPLES },
  ]);
  return row + 1;
}

function addGovernanceTextRow(ws, row, label, text) {
  setCell(ws, row, COL_QUESTION, label, { align: "left", vertical: "top" });
  ws.mergeCells(row, COL_P_START, row, COL_P_END);
  setCell(ws, row, COL_P_START, text || "", { align: "left", vertical: "top" });
  for (let col = COL_P_START + 1; col <= COL_P_END; col++) {
    borderCell(ws.getCell(row, col));
  }
  setAutoRowHeight(ws, row, [
    { text: label, width: COL_WIDTH_QUESTION },
    { text: text || "", width: COL_WIDTH_MERGED_PRINCIPLES },
  ]);
  return row + 1;
}

function addReviewSection(ws, row, gl) {
  const q10Label = "10. Details of Review of NGRBCs by the Company:";
  ws.mergeCells(row, COL_QUESTION, row, COL_FREQ_END);
  setCell(ws, row, COL_QUESTION, q10Label, {
    align: "left",
    bold: true,
    vertical: "top",
  });
  for (let col = COL_QUESTION + 1; col <= COL_FREQ_END; col++) {
    borderCell(ws.getCell(row, col));
  }
  setAutoRowHeight(ws, row, [{ text: q10Label, width: COL_WIDTH_FULL_TABLE }]);
  row += 1;

  const reviewHeader =
    "Indicate whether review was undertaken by Director / Committee of the Board/ Any other Committee";
  const freqHeader =
    "Frequency (Annually / Half yearly / Quarterly / Any other - please specify)";

  setCell(ws, row, COL_QUESTION, "", { fill: TABLE_HEADER_FILL });
  ws.mergeCells(row, COL_P_START, row, COL_P_END);
  setCell(ws, row, COL_P_START, reviewHeader, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
    vertical: "top",
  });
  ws.mergeCells(row, COL_FREQ_START, row, COL_FREQ_END);
  setCell(ws, row, COL_FREQ_START, freqHeader, {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
    vertical: "top",
  });
  setAutoRowHeight(ws, row, [
    { text: reviewHeader, width: COL_WIDTH_MERGED_PRINCIPLES },
    { text: freqHeader, width: COL_WIDTH_MERGED_PRINCIPLES },
  ]);
  row += 1;

  setCell(ws, row, COL_QUESTION, "Subject for Review", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "left",
  });
  for (let i = 0; i < PRINCIPLE_COUNT; i++) {
    setCell(ws, row, COL_P_START + i, `P${i + 1}`, {
      bold: true,
      fill: TABLE_HEADER_FILL,
      align: "center",
    });
    setCell(ws, row, COL_FREQ_START + i, `P${i + 1}`, {
      bold: true,
      fill: TABLE_HEADER_FILL,
      align: "center",
    });
  }
  row += 1;

  const reviewSubjects = [
    {
      label: "Performance against above policies and follow up action",
      reviewObj: gl?.performanceReviewDirector,
      freqObj: gl?.frequencyReviewDirector,
    },
    {
      label:
        "Compliance with statutory requirements of relevance to the principles, and, rectification of any non-compliances",
      reviewObj: gl?.performanceCompliance,
      freqObj: gl?.frequencyCompliance,
    },
  ];

  reviewSubjects.forEach(({ label, reviewObj, freqObj }) => {
    setCell(ws, row, COL_QUESTION, label, { align: "left", vertical: "top" });
    fillPrincipleRow(ws, row, getPrincipleValues(reviewObj, formatReviewValue));
    fillPrincipleRow(ws, row, getPrincipleValues(freqObj, (v) => (v ? String(v) : "")), COL_FREQ_START);
    setAutoRowHeight(ws, row, [{ text: label, width: COL_WIDTH_QUESTION }]);
    row += 1;
  });

  return row;
}

function addIndependentAssessmentSection(ws, row, gl) {
  const q11Label =
    "11. Has the entity carried out independent assessment/ evaluation of the working of its policies by an external agency? (Yes/No). If yes, provide name of the agency.";
  setCell(ws, row, COL_QUESTION, q11Label, { align: "left", vertical: "top" });
  fillPrincipleRow(
    ws,
    row,
    getPrincipleValues(gl?.extenalAgencyPolicyEvaluation)
  );
  setAutoRowHeight(ws, row, [{ text: q11Label, width: COL_WIDTH_QUESTION }]);
  return row + 1;
}

function addReasonsTable(ws, row, gl) {
  const q12Label =
    '12. If answer to question (1) above is "No" i.e. not all Principles are covered by a policy, reasons to be stated:';
  ws.mergeCells(row, COL_QUESTION, row, COL_P_END);
  setCell(ws, row, COL_QUESTION, q12Label, {
    align: "left",
    bold: true,
    vertical: "top",
  });
  for (let col = COL_QUESTION + 1; col <= COL_P_END; col++) {
    borderCell(ws.getCell(row, col));
  }
  setAutoRowHeight(ws, row, [
    { text: q12Label, width: COL_WIDTH_QUESTION + COL_WIDTH_MERGED_PRINCIPLES },
  ]);
  row += 1;

  setCell(ws, row, COL_QUESTION, "Questions", {
    bold: true,
    fill: TABLE_HEADER_FILL,
    align: "center",
  });
  for (let i = 0; i < PRINCIPLE_COUNT; i++) {
    setCell(ws, row, COL_P_START + i, `P${i + 1}`, {
      bold: true,
      fill: TABLE_HEADER_FILL,
      align: "center",
    });
  }
  row += 1;

  const reasonRows = [
    {
      label: "The entity does not consider the Principles material to its business.",
      obj: gl?.nonMaterial,
    },
    {
      label:
        "The entity is not at a stage where it is in a position to formulate and implement the policies on specified principles.",
      obj: gl?.notReady,
    },
    {
      label:
        "The entity does not have financial or human or technical resources available for the task.",
      obj: gl?.resourceConstraints,
    },
    {
      label: "It is planned to be done in the next financial year.",
      obj: gl?.plannedNextYear,
    },
    {
      label: "Any other reason (please specify)",
      obj: gl?.reasonRemark,
      isText: true,
    },
  ];

  reasonRows.forEach(({ label, obj, isText }) => {
    setCell(ws, row, COL_QUESTION, label, { align: "left", vertical: "top" });
    fillPrincipleRow(
      ws,
      row,
      getPrincipleValues(obj, isText ? (v) => (v ? String(v) : "-") : toYesNoNa)
    );
    setAutoRowHeight(ws, row, [{ text: label, width: COL_WIDTH_QUESTION }]);
    row += 1;
  });

  return row;
}

export function buildMgtAndProcessDisclosureSheet(workbook, sectionB) {
  const ws = workbook.addWorksheet("MGT and process disclosure", {
    views: [{ showGridLines: true }],
  });

  const widths = {
    A: 2,
    B: 2,
    C: 2,
    D: 40,
    E: 10,
    F: 10,
    G: 10,
    H: 10,
    I: 10,
    J: 10,
    K: 10,
    L: 10,
    M: 10,
    N: 10,
    O: 10,
    P: 10,
    Q: 10,
    R: 10,
    S: 10,
    T: 10,
    U: 10,
    V: 10,
  };
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });

  const pm = sectionB?.policyAndManagement || {};
  const gl = sectionB?.governanceLeadership || {};

  let row = 3;
  styleSectionTitle(ws, row, "SECTION B: MANAGEMENT AND PROCESS DISCLOSURES", COL_FREQ_END);
  row += 1;
  styleSubSectionTitle(ws, row, "I. POLICY AND MANAGEMENT PROCESSES", COL_FREQ_END);
  row += 1;
  row = addPolicyHeaderRow(ws, row);

  row = addQuestionPrincipleRow(
    ws,
    row,
    "1. (a) Whether your entity's policy/policies cover each principle and its core elements of the NGRBCs.",
    getPrincipleValues(pm?.policyCoveringPrinciples)
  );

  row = addQuestionPrincipleRow(
    ws,
    row,
    "1. (b) Has the policy been approved by the Board?",
    getPrincipleValues(pm?.boardApprovedPolicies)
  );

  setCell(ws, row, COL_QUESTION, "1. (c) Web Link of the Policies, if available", {
    align: "left",
    vertical: "top",
  });
  getPrincipleValues(pm?.webLinksForPolicies, (v) => v || "-").forEach((value, i) => {
    const col = COL_P_START + i;
    const url = pm?.webLinksForPolicies?.[principleKey(i)];
    if (url) {
      setCell(ws, row, col, url, { align: "left", link: url });
    } else {
      setCell(ws, row, col, "-", { align: "center" });
    }
  });
  setAutoRowHeight(ws, row, [
    { text: "1. (c) Web Link of the Policies, if available", width: COL_WIDTH_QUESTION },
  ]);
  row += 1;

  const translated = toYesNoNa(pm?.policiesTranslatedToProcedures);
  row = addQuestionPrincipleRow(
    ws,
    row,
    "2. Whether the entity has translated the policy into procedures.",
    Array(PRINCIPLE_COUNT).fill(translated)
  );

  row = addQuestionPrincipleRow(
    ws,
    row,
    "3. Do the enlisted policies extend to your value chain partners? (Yes/No)",
    getPrincipleValues(pm?.policiesExtendToValueChainPartners)
  );

  row = addTextOrDetailsRow(
    ws,
    row,
    "4. Name of the national and international codes/certifications/labels/standards (e.g. Forest Stewardship Council, Fairtrade, Rainforest Alliance, Trustea) standards (e.g. SA 8000, OHSAS, ISO, BIS) adopted by your entity and mapped to each principle.",
    pm?.codesCertificationsLabelsStandards
  );

  row = addTextOrDetailsRow(
    ws,
    row,
    "5. Specific commitments, goals and targets set by the entity with defined timelines, if any.",
    pm?.commitmentsGoalsTargets
  );

  row = addTextOrDetailsRow(
    ws,
    row,
    "6. Performance of the entity against the specific commitments, goals and targets along-with reasons in case the same are not met.",
    pm?.performanceAgainstGoals
  );

  styleSubSectionTitle(ws, row, "II. GOVERNANCE, LEADERSHIP AND OVERSIGHT", COL_FREQ_END);
  row += 1;

  row = addGovernanceTextRow(
    ws,
    row,
    "7. Statement by director responsible for the business responsibility report, highlighting ESG related challenges, targets and achievements (listed entity has flexibility regarding the placement of this disclosure).",
    gl?.directorStatementESG
  );

  row = addGovernanceTextRow(
    ws,
    row,
    "8. Details of the highest authority responsible for implementation and oversight of the Business Responsibility policy (ies).",
    gl?.oversightBusinessResponsibility
  );

  const committeeText = gl?.independentAssessment?.status
    ? `Yes${gl?.independentAssessment?.remark ? ` - ${gl.independentAssessment.remark}` : ""}`
    : "No";
  row = addGovernanceTextRow(
    ws,
    row,
    "9. Does the entity have a specified Committee of the Board/ Director responsible for decision making on sustainability related issues? (Yes / No). If Yes, provide details.",
    committeeText
  );

  row = addReviewSection(ws, row, gl);
  row = addIndependentAssessmentSection(ws, row, gl);
  addReasonsTable(ws, row, gl);

  return ws;
}
